import axios from 'axios';
import { getApiUrl } from '../util/getServerInfo';
import { debugWarn } from '../util/debug';
import { getBackend } from '../backend';
import { expireSession } from './AuthActions';
import {
  addSeconds, applyLookback, windowSeconds, DEFAULT_FILTERS,
  snapSpan, floorToSpan, pageWindow, secondsBetween,
} from '../util/mcpbTime';
import { getVisibleCameras } from '../util/camSets';
import { normalizeSlotCamera } from '../util/cameraDrawer';
import {
  emptyDayDots, orDayDots, dayDotsFromResult, cameraSetSignature, monthKey, monthRange,
  isUnsupportedPresenceError,
} from '../util/mcpbPresence';
import { isMultiServerSession } from '../util/sessionMode';
import {
  SET_MCPB_CONF,
  SET_MCPB_SLOTS,
  SET_MCPB_SLOT_CAMERA,
  SET_MCPB_TRACK_EVENTS,
  SET_MCPB_EVENTS_LOADING,
  SET_MCPB_EVENTS_ERROR,
  SET_MCPB_SEARCH,
  SET_MCPB_PLAYHEAD,
  SET_MCPB_EVENT_FLAG,
  RESET_MCPB,
} from './types';

// ── Plain action creators ─────────────────────────────────────────────
export const setMcpbConf = (conf) => ({ type: SET_MCPB_CONF, payload: conf });
export const setMcpbSlots = (slots) => ({ type: SET_MCPB_SLOTS, payload: slots });
export const setMcpbSlotCamera = (index, camera) => ({ type: SET_MCPB_SLOT_CAMERA, payload: { index, camera } });
export const setMcpbSearch = ({ selectedTimestamp, window, filters }) =>
  ({ type: SET_MCPB_SEARCH, payload: { selectedTimestamp, window, filters } });
// Persist the playhead offset (seconds from window start) so a same-session nav away + back restores
// the exact moment (1g). Wiped with the slice on a server change.
export const setMcpbPlayhead = (offsetSec) => ({ type: SET_MCPB_PLAYHEAD, payload: offsetSec });
export const resetMcplayback = () => ({ type: RESET_MCPB });

// ── Multi-cam event fetch (the core data thunk) ───────────────────────
// Generation token + AbortController guard against stale shard responses landing after fresh ones
// (dview-lite data-fetching rule). Module-scoped so successive loads share the same counter/controller.
let _gen = 0;
let _abort = null;

// Resolve the per-NVR request target. eview cross-server clips carry their own session+URL in
// state.server.serverSessions[serial] ({sSess, serverUrl}); the local/dview case uses the current
// server's API URL + auth session.
const resolveServer = (state, serial) => {
  const sessions = (state.server && state.server.serverSessions) || {};
  const entry = sessions[serial];
  if (entry && entry.serverUrl) {
    // eView MEMBER (slave) NVRs are session-less by design — the controller holds the only
    // session. Authenticate their event/playback RPCs with the logged-in user:pass, exactly
    // like the direct video streams (user=&pass= URL auth). The backend accepts a "user:pass"
    // first param for modEvent and gates it on the Playback right (thread_xmlrpc.py). Passing
    // a null session made a member answer "noauth", which the global session interceptor
    // mistook for the CONTROLLER's session expiring and tore the session down mid-Playback
    // (blank grid / "Loading recorded video…"). (#86bb7phhh)
    const auth = entry.sSess
      || (state.auth && state.auth.username && state.auth.password
            ? `${state.auth.username}:${state.auth.password}`
            : (state.auth && state.auth.sSess));
    return { apiUrl: entry.serverUrl, sSess: auth };
  }
  return { apiUrl: getApiUrl(), sSess: state.auth && state.auth.sSess };
};

// Merge a fetch's per-camera events into the stored map when both are for the SAME window, so a camera
// set swap (1-4 -> 5-8) keeps the other sets' events instead of throwing them away (86bc8571y). The
// fetched cameras are authoritative (an explicit [] when none came back), except those whose NVR failed
// or sent a non-array reply this time, which keep what they had. A different window replaces the map, as it always has.
const mergeSameWindow = (state, windowKey, fetched, cams, results) => {
  const cur = state.mcplayback || {};
  if (cur.trackEventsWindow !== windowKey || !cur.trackEvents) return fetched;
  const okSerials = new Set(results.filter((r) => !r.failed && !r.invalid).map((r) => String(r.serial)));
  const merged = { ...cur.trackEvents };
  cams.forEach((c) => {
    if (okSerials.has(String(c.bSerial))) merged[`${c.bSerial}_${c.bCamera}`] = [];
  });
  Object.keys(fetched).forEach((k) => { merged[k] = fetched[k]; });
  return merged;
};

/**
 * loadMultiCamEvents — fetch recorded events for all filled slots over a time window.
 *
 * Groups filled slots by NVR serial and issues ONE `event.getEventsByTime` per NVR (not per camera),
 * then merges the flat results into a map keyed `${serial}_${cameraId}` and dispatches it as trackEvents.
 *
 * Wire details CONFIRMED against the codebase (1c, 3 independent sources):
 *   - param order `[sSess, rgbCamera[], startYMDHMS, endYMDHMS, (fIncludeSmartFill)]` — the JSON-RPC
 *     layer consumes params[0] as the session (rda-backend event.getEventsByTime takes no sSess; legacy
 *     lite getVideo + Java MCEventModel both pass sSess first).
 *   - envelope: getEventsByTime returns the FLAT rgsEvent list (event.py:193); the framework wraps the
 *     module return at result[1] (legacy getVideo reads response.data.result[1]). So result[1] = flat list.
 *   - fIncludeSmartFill is APPENDED ONLY WHEN TRUE: backends predating 2026-04-27 expose a 4-arg
 *     signature and a 5th positional raises a Python TypeError that aborts the search (Java
 *     MCEventModel omits it on legacy via supportsSmartFillEventQuery). false == the backend default,
 *     so omitting it loses nothing. (Smart-fill underlay rendering lands at 1f/2a.)
 * NOTE: getEventsByTime has NO flag/tag params — it returns ALL events in the window. The dt-modal
 * "Find events with" filters are applied CLIENT-SIDE (off sTags/fArchive/fExport), matching Java.
 *
 * @param {object} opts
 * @param {string} opts.startTimestamp  NVR-local 'YYYYMMDDHHMMSS' (per-server tz conversion by the caller)
 * @param {string} opts.endTimestamp    NVR-local 'YYYYMMDDHHMMSS'
 * @param {boolean} [opts.fIncludeSmartFill]
 */
export const loadMultiCamEvents = ({ startTimestamp, endTimestamp, fIncludeSmartFill = false } = {}) =>
  (dispatch, getState) => {
    const gen = ++_gen;
    if (_abort) _abort.abort();
    _abort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const signal = _abort ? _abort.signal : undefined;

    const state = getState();
    const backend = getBackend(state);
    const filled = (state.mcplayback.slots || []).filter((cam) => cam && (backend?.kind !== 'cloud' || cam.sDeviceID));

    if (filled.length === 0) {
      dispatch({ type: SET_MCPB_TRACK_EVENTS, payload: {} });
      return Promise.resolve({});
    }

    // CLOUD product: recorded events come from cloudapi `/ve/playbackevents`, not a
    // per-NVR JSON-RPC. Route through the CloudNvrAdapter (one fetch per filled
    // camera; the adapter handles device-tz<->UTC + clip-URL resolution). The
    // trackEvents key stays `${bSerial}_${bCamera}` (cloud bSerial is 0) so the
    // engine (unionTrackClips / SlotCell) consumes it unchanged. Mirrors the JSON-RPC
    // branch's generation-token guard against stale results.
    if (backend && backend.kind === 'cloud' && backend.fetchPlaybackEvents) {
      dispatch({ type: SET_MCPB_EVENTS_LOADING, payload: true });
      console.debug('[MCPlayback cloud] fetch events', `[${startTimestamp}-${endTimestamp}]`,
        filled.map((c) => `${c.sDeviceID}:${c.bCamera}`).join(' '));
      const reqs = filled.map((cam) =>
        backend.fetchPlaybackEvents(cam.sDeviceID, cam.bCamera, startTimestamp, endTimestamp)
          .then((clips) => ({ cam, clips }))
          .catch((err) => {
            console.error('[MCPlayback cloud] fetch failed', cam.sDeviceID, cam.bCamera, (err && err.message) || err);
            return { cam, clips: null, error: true };
          }));
      return Promise.all(reqs).then((results) => {
        if (gen !== _gen) return null; // a newer load started — drop this stale result
        const trackEvents = {};
        let anyError = false;
        results.forEach(({ cam, clips, error }) => {
          if (error) anyError = true;
          trackEvents[`${cam.bSerial}_${cam.bCamera}`] = clips || [];
        });
        const total = Object.keys(trackEvents).reduce((n, k) => n + trackEvents[k].length, 0);
        console.info(`[MCPlayback cloud] events loaded: ${total} for [${startTimestamp}-${endTimestamp}]`);
        dispatch({ type: SET_MCPB_TRACK_EVENTS, payload: trackEvents });
        if (anyError && total === 0) dispatch({ type: SET_MCPB_EVENTS_ERROR, payload: true });
        dispatch({ type: SET_MCPB_EVENTS_LOADING, payload: false });
        return trackEvents;
      });
    }

    // Group by serial → one RPC per NVR.
    const bySerial = {};
    filled.forEach((cam) => {
      (bySerial[cam.bSerial] = bySerial[cam.bSerial] || []).push(cam);
    });

    dispatch({ type: SET_MCPB_EVENTS_LOADING, payload: true });
    // Diagnostics: log the exact query (cams per NVR + window). Pairs with the completion line below so
    // "no clips show" is one glance — did we ask for the right cams/window, and did anything come back.
    console.debug('[MCPlayback] fetch events',
      `[${startTimestamp}-${endTimestamp}]`,
      Object.keys(bySerial).map((s) => `${s}:[${bySerial[s].map((c) => c.bCamera).join(',')}]`).join(' '));

    const requests = Object.keys(bySerial).map((serial) => {
      const cams = bySerial[serial];
      const { apiUrl, sSess } = resolveServer(state, serial);
      const params = [sSess, cams.map((c) => c.bCamera), startTimestamp, endTimestamp];
      if (fIncludeSmartFill) params.push(true); // omit on legacy 4-arg backends; false == default
      const reqBody = { jsonrpc: 2.0, method: 'event.getEventsByTime', id: 201, params };
      return axios({
        method: 'post',
        url: apiUrl,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        data: reqBody,
        timeout: 8000,
        signal,
      }).then((res) => {
        // result[1] is the flat rgsEvent list on success, but a non-array on failure (e.g. the
        // "noauth" string). Coerce to [] so one bad/offline member NVR can't crash the whole
        // multi-cam merge below (events.forEach). (#86bb7phhh)
        const raw = res.data && res.data.result && res.data.result[1];
        // `invalid`: a same-window merge keeps what this NVR had (a camera set's prefetched events)
        // instead of clearing it (86bc8571y). It still publishes as an empty answer, as before.
        return { serial, events: Array.isArray(raw) ? raw : [], invalid: !Array.isArray(raw) };
      }).catch((err) => {
        // Per-call catch. The .then above only handled a bad PAYLOAD; an axios REJECTION (member
        // offline, DNS/CORS, 8s timeout) still rejected the bare Promise.all below, so a single
        // unreachable eView member discarded every healthy member's clips and left the grid blank
        // under an error flag. Its sibling getPostLoginFromServer already got this treatment
        // (c88df088d); this fan-out did not. (#86bb7phhh)
        debugWarn(`[MCPlayback] events fetch failed for serial ${serial}:`, (err && err.message) || err);
        return { serial, events: [], failed: true };
      });
    });

    return Promise.all(requests)
      .then((results) => {
        if (gen !== _gen) return null; // a newer load started — drop this stale result
        const trackEvents = {};
        results.forEach(({ serial, events }) => {
          events.forEach((ev) => {
            const key = `${serial}_${ev.bCamera}`;
            (trackEvents[key] = trackEvents[key] || []).push(ev);
          });
        });
        const total = Object.keys(trackEvents).reduce((n, k) => n + trackEvents[k].length, 0);
        const failed = results.filter((r) => r.failed);
        console.info(`[MCPlayback] events loaded: ${total} for [${startTimestamp}-${endTimestamp}]`,
          results.map((r) => `${r.serial}:${r.failed ? 'FAILED' : r.events.length}`).join(' '));
        const allFailed = results.length > 0 && failed.length === results.length;
        // A TOTAL failure keeps whatever is already on screen — overwriting with {} would blank a
        // working grid over a transient blip (asserted by the sibling test above). A PARTIAL failure
        // publishes what DID answer, which is the whole point of the per-call catch: one unreachable
        // member must not cost every healthy member its clips.
        if (!allFailed) {
          const windowKey = `${startTimestamp}|${endTimestamp}`;
          dispatch({
            type: SET_MCPB_TRACK_EVENTS,
            payload: mergeSameWindow(getState(), windowKey, trackEvents, filled, results),
            window: windowKey,
          });
        }
        dispatch({ type: SET_MCPB_EVENTS_ERROR, payload: allFailed });
        dispatch({ type: SET_MCPB_EVENTS_LOADING, payload: false });
        return allFailed ? null : trackEvents;
      })
      .catch((err) => {
        if (gen !== _gen) return null; // stale or aborted — ignore
        // A real failure (network/CORS/timeout/RPC error) — surface it (flag + log) instead of silently
        // leaving an empty grid that looks identical to a no-events result.
        console.error('[MCPlayback] event fetch failed:', (err && err.message) || err);
        dispatch({ type: SET_MCPB_EVENTS_ERROR, payload: true });
        dispatch({ type: SET_MCPB_EVENTS_LOADING, payload: false });
        return null;
      });
  };

// ── Playback clip auth-failure → session-expired surfacing ────────────
// A playback clip plays through a native `<video src=pbserverUrl>`. When pbserver rejects the session it
// answers that media fetch with an auth status — but the <video> only exposes a generic MediaError
// (code 4), and that fetch never passes through axios, so the global noauth→EXPIRE_SESSION interceptor
// never sees it and the user is left with a misleading "Can't play this clip". pbserver can reject a
// session that rda-backend + mpengine still accept (live streams fine, events load, but the clip fails),
// so a JSON-RPC probe would wrongly report the session healthy. The ONLY authoritative signal is
// pbserver's own status, so on a clip error we RE-FETCH the same clip URL and read it.
//
// The status is 401 (#86bb7phhh). The C pbserver every Docker NVR runs answers cmd=fetch with
// `send_error(sock, 401, "Unauthorized", "Auth Failed")` (handler.c:534) and "No Auth Given" also 401
// (:526); there is no 500 anywhere on that path. This used to key on 500, which is the LEGACY Python
// pbserver's code (request_thread.py:190/234) — so the check was dead on the entire Docker fleet, AND on
// legacy boxes it over-fired, because that server also returns 500 for "Invalid Request", "No Filename
// Given", "Unknown command" and `stream_thread.py:518` "Bad File". A corrupt clip logged the user out.
// Verified live on cs2427 and cs256: a bogus session on cmd=fetch returns 401.
//
// SCOPE: only a clip served by the NVR that minted our session proves anything about that session. An
// eView MEMBER clip authenticates with user:pass in the URL, not sSess, so a 401 there means the member
// rejected the CREDENTIALS, not that the controller session expired — expiring on it would log the user
// out of a healthy session. Members are skipped.
//
// We abort right after the response headers so a still-good clip (200 → a streaming MP4) is never
// actually downloaded (async + AbortController only — never a sync XHR, which froze the tab).
// Cooldown-guarded so N cams erroring in one burst fire ONE check. Local/eview only — cloud playback
// (REST/JWT) is a separate phase.
let _lastSessionProbeAt = 0;
const SESSION_PROBE_COOLDOWN_MS = 5000; // matches the interceptor's expiry cooldown

/**
 * Did pbserver reject our credentials for this clip?
 *
 * Two server generations, two answers:
 *   C pbserver (every Docker NVR)  — 401, on both no-auth and bad-auth (handler.c:526/534).
 *   Legacy Python (RPM NVRs)       — 500 with the status line "Auth Failed" (request_thread.py:190/234).
 *
 * The 500 MUST be qualified by that message. The whole reason this check moved off a bare 500 is that
 * the legacy server also returns 500 for "Invalid Request", "No Filename Given", "Unknown command" and
 * stream_thread.py's "Bad File" — so an unqualified 500 logged the user out over a corrupt clip. RPM is
 * still a supported deployment (root CLAUDE.md), so dropping it entirely would leave those users stuck
 * on "Can't play this clip" with no way to learn their session died. #86bb7phhh
 */
export const isPlaybackAuthReject = (status, statusText = '') =>
  status === 401 || (status === 500 && /auth\s*failed/i.test(String(statusText || '')));

/**
 * Does this clip URL carry OUR session? Only then does an auth reject implicate our session.
 * A member URL uses `user=&pass=`; the serving NVR's uses `session=`.
 */
export const clipUrlUsesOurSession = (clipUrl, sSess) =>
  !!sSess && !!clipUrl && String(clipUrl).indexOf(`session=${sSess}`) >= 0;

export const verifyPlaybackSession = (clipUrl) => (dispatch, getState) => {
  const now = Date.now();
  if (now - _lastSessionProbeAt < SESSION_PROBE_COOLDOWN_MS) return Promise.resolve();
  const state = getState();
  const backend = getBackend(state);
  if (backend && backend.kind === 'cloud') return Promise.resolve();
  const sSess = state.auth && state.auth.sSess;
  if (!sSess || !clipUrl || typeof fetch === 'undefined') return Promise.resolve();
  // An eView member clip carries user:pass, not our session — a 401 there is not our session's verdict.
  if (!clipUrlUsesOurSession(clipUrl, sSess)) return Promise.resolve();
  _lastSessionProbeAt = now;
  const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
  const to = setTimeout(() => { if (ctrl) ctrl.abort(); }, 6000);
  return fetch(clipUrl, ctrl ? { signal: ctrl.signal } : undefined).then((r) => {
    clearTimeout(to);
    if (ctrl) ctrl.abort(); // we only needed the status line — never download the clip body
    if (r && isPlaybackAuthReject(r.status, r.statusText)) {
      // pbserver auth reject — this session can't play back. Surface it via the existing session-expired
      // flow (clears session → /login → SessionExpiredModal) instead of the misleading "Can't play" text.
      console.warn(`[MCPlayback] clip auth failed (HTTP ${r.status}) — session invalid for playback; expiring session`);
      dispatch(expireSession(sSess));
    }
  }, () => { clearTimeout(to); /* network error is not a session verdict — keep the clip message */ });
};

// Test-only: reset the probe cooldown so successive it() blocks in one file each get a fresh probe.
export const _resetSessionProbeForTesting = () => { _lastSessionProbeAt = 0; };

/**
 * setMcpbEventFlag — toggle the archive (flag 1) / export (flag 2) flag on one event, OPTIMISTICALLY.
 *
 * Dispatches the optimistic local flip FIRST (the Archive/Export button reflects it instantly — they
 * double as status), then fires `event.setEventFlag` and REVERTS on failure. The View shows the toast +
 * Undo; Undo just calls this again with the inverse value.
 *
 * Wire details VERIFIED against the codebase:
 *   - JSON-RPC params `[sSess, bID, bFlag, fEnable]` — sSess prepends like every event.* call (Java
 *     Event.java:251 `event.setEventFlag` with `{ sAuth, bID, EventFlags.ARCHIVE, fArchive }`; the
 *     rda-backend module sig `setEventFlag(self, bID, bFlag, fEnable)` consumes params[0] as the session).
 *   - flags: FLAG_ARCHIVE=1 / FLAG_EXPORT=2 (rda-backend/src/db/flags.py; EventFlags.ARCHIVE/EXPORT).
 *   - the clip's own server: eview cross-server clips flag on THEIR server (resolveServer by serial),
 *     mirroring loadMultiCamEvents; dview/local uses the current server + session.
 *
 * CLOUD product: there is no per-NVR JSON-RPC — route through the CloudNvrAdapter's `/ve/flagevent`
 * (cloudapi stores a single `fFlagged` bitmask: 1=archive, 2=export, 3=both). Same optimistic-flip +
 * revert-on-failure contract; `deviceId` (the clip's cloud device) is required for the endpoint's
 * device-scoped auth, and the JWT comes from Redux (state.auth.sSess). Dragon + legacy share this path.
 *
 * @param {object} opts
 * @param {number} opts.bID    the event id to flag
 * @param {number} opts.flag   1 (archive) | 2 (export)
 * @param {boolean} opts.value enable (true) / clear (false)
 * @param {number} [opts.serial] the clip's NVR serial (routes eview cross-server flags; omit = current)
 * @param {string} [opts.deviceId] the clip's cloud device id (CLOUD only — /ve/flagevent device scope)
 * @returns {Promise<{ok:boolean}>}
 */
export const setMcpbEventFlag = ({ bID, flag, value, serial, deviceId }) =>
  (dispatch, getState) => {
    // Optimistic: flip local state now so the button reflects it before the round-trip.
    // `serial` scopes the flip to the NVR that owns this event. Event bIDs are per-NVR
    // auto-increment, so without it an eView grid flips the same bID on EVERY member. #86bb7phhh
    dispatch({ type: SET_MCPB_EVENT_FLAG, payload: { bID, flag, value, serial } });

    const state = getState();

    // CLOUD: flag through cloudapi /ve/flagevent (single fFlagged bitmask), not a nonexistent NVR RPC
    // (which was the "couldn't reach the server" toast). Revert the optimistic flip on failure, same as
    // the JSON-RPC path.
    const backend = getBackend(state);
    if (backend && backend.kind === 'cloud' && backend.flagEvent) {
      const token = (state.auth && state.auth.sSess) || undefined;
      return backend.flagEvent({ bID, flag, value, deviceId, token })
        .then((res) => {
          if (!res || !res.ok) {
            dispatch({ type: SET_MCPB_EVENT_FLAG, payload: { bID, flag, value: !value, serial } });
            return { ok: false };
          }
          return { ok: true };
        })
        .catch(() => {
          dispatch({ type: SET_MCPB_EVENT_FLAG, payload: { bID, flag, value: !value, serial } });
          return { ok: false };
        });
    }

    const { apiUrl, sSess } = resolveServer(state, serial);
    const reqBody = { jsonrpc: 2.0, method: 'event.setEventFlag', id: 202, params: [sSess, bID, flag, !!value] };

    return axios({
      method: 'post',
      url: apiUrl,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      data: reqBody,
      timeout: 8000,
    })
      .then((res) => {
        if (res && res.data && res.data.error) throw new Error('setEventFlag rpc error');
        return { ok: true };
      })
      .catch(() => {
        // Revert the optimistic flip — the backend did not take it.
        dispatch({ type: SET_MCPB_EVENT_FLAG, payload: { bID, flag, value: !value, serial } });
        return { ok: false };
      });
  };

/**
 * runMcpbSearch — the dt-modal "Go" entry point. Given the user's anchor time (= window START) and
 * the chosen "Show window" + filters, this CALCULATES the [start, end] span and SENDS it:
 *   1. snap the anchor back if it is within 10 min of the NVR clock (recent clips need disk-write
 *      time — `applyLookback`, mirrors legacy SearchContainer.js:115-122),
 *   2. commit {selectedTimestamp, window, filters} to the slice (drives the watch-chip + the 1f
 *      timeline span / client-side filtering),
 *   3. fetch all events in [start, start+window] via loadMultiCamEvents.
 * The NVR "now" is read from state.utility.nvrTimestampShort ('YYYYMMDDHHMMSS'), never the browser.
 *
 * A CUSTOM range (Window|Custom toggle) arrives with a NUMERIC `window` (= seconds) and an EXPLICIT end.
 * Its lookback is SKIPPED: snapping the start back would drag the explicit end the same 10 min early.
 * Preset keys keep the snap (recent presets still need disk-write time).
 *
 * @param {object} opts
 * @param {string} opts.anchorTimestamp  NVR-local 'YYYYMMDDHHMMSS' from the modal's date + time inputs
 * @param {string|number} opts.window    'Show window' preset key (5min|15min|1hr|6hr|24hr) OR custom seconds
 * @param {object} opts.filters          'Find events with' set
 */
export const runMcpbSearch = ({ anchorTimestamp, window: win, filters }) =>
  (dispatch, getState) => {
    const nowTs = (getState().utility || {}).nvrTimestampShort || '';
    const isCustom = typeof win === 'number';
    const start = isCustom ? anchorTimestamp : applyLookback(anchorTimestamp, nowTs);
    const end = addSeconds(start, windowSeconds(win));
    dispatch(setMcpbSearch({ selectedTimestamp: start, window: win, filters }));
    return dispatch(loadMultiCamEvents({ startTimestamp: start, endTimestamp: end }));
  };

/**
 * refetchMcpbEvents — re-run the committed search over its existing window. Called after the slot
 * set changes (a camera was picked/unassigned in the drawer) so the new slot's events load without
 * the user re-opening the dt-modal. Reads the already-committed {selectedTimestamp, window} — the
 * anchor was snapped at search time, so NO lookback is re-applied here. No-op when nothing has been
 * searched yet (an empty grid has no window to fetch).
 */
export const refetchMcpbEvents = () => (dispatch, getState) => {
  const { selectedTimestamp, window: win } = getState().mcplayback || {};
  if (!selectedTimestamp) return Promise.resolve(null);
  const end = addSeconds(selectedTimestamp, windowSeconds(win));
  return dispatch(loadMultiCamEvents({ startTimestamp: selectedTimestamp, endTimestamp: end }));
};

// Prefetch bounds: getEventsByTime returns EVERY event in the window, so a wide span (8-24 h across
// a 16-camera view) is a large reply that would compete with video on a store uplink.
export const PREFETCH_MAX_WINDOW_SEC = 4 * 3600;
export const PREFETCH_MAX_CAMERAS = 64;
// Cameras with a prefetch in flight, as `window|serial_camera`: rapid set swaps (each one's own fetch
// resolving) must not ask for the same cameras twice over the store uplink. A different view in the same
// window asks for its own cameras, so it still runs.
const _prefetchInFlight = new Set();

/**
 * prefetchViewEvents — in the background, fetch the committed window's events for every camera in the
 * current VIEW that is not on screen, and merge them in, so paging the camera set (1-4 -> 5-8) finds
 * its events already loaded and each tile can request its clip at once instead of waiting on another
 * RPC first (86bc8571y: 0.6-2.4 s per swap on a store link). One RPC per NVR. Never touches the
 * foreground loading / error flags, and drops its result if the window moved on while it was in flight.
 * Local and eView only; the cloud product fetches per camera over REST.
 */
export const prefetchViewEvents = () => (dispatch, getState) => {
  const state = getState();
  const st = state.mcplayback || {};
  const win = windowSeconds(st.window);
  if (!st.selectedTimestamp || !(win > 0) || win > PREFETCH_MAX_WINDOW_SEC) return Promise.resolve(null);
  const backend = getBackend(state);
  if (backend && backend.kind === 'cloud') return Promise.resolve(null);
  const start = st.selectedTimestamp;
  const end = addSeconds(start, win);
  const windowKey = `${start}|${end}`;
  const currentSerial = isMultiServerSession(state)
    ? (state.server.activeServerSerial || state.server.bSerial)
    : state.server.bSerial;
  const key = (c) => `${c.bSerial}_${c.bCamera}`;
  const onScreen = new Set((st.slots || []).filter(Boolean).map(key));
  const cached = st.trackEventsWindow === windowKey ? (st.trackEvents || {}) : {};
  const cams = (state.server.cameras || [])
    .map((c) => normalizeSlotCamera(c, { currentSerial }))
    .filter((c) => c && c.fEnable !== false && c.bCamera != null && !onScreen.has(key(c)) && !cached[key(c)])
    .filter((c) => !_prefetchInFlight.has(`${windowKey}|${key(c)}`))
    .slice(0, PREFETCH_MAX_CAMERAS);
  if (!cams.length) return Promise.resolve(null);
  const inFlight = cams.map((c) => `${windowKey}|${key(c)}`);
  inFlight.forEach((k) => _prefetchInFlight.add(k));

  const bySerial = {};
  cams.forEach((c) => { (bySerial[c.bSerial] = bySerial[c.bSerial] || []).push(c); });
  const requests = Object.keys(bySerial).map((serial) => {
    const { apiUrl, sSess } = resolveServer(state, serial);
    // Another NVR (an eView member) is any target that is not this app's own backend; the controller's
    // serverSessions entry points at getApiUrl() too, so its noauth still ends the session.
    const isMember = apiUrl !== getApiUrl();
    const reqBody = { jsonrpc: 2.0, method: 'event.getEventsByTime', id: 202, params: [sSess, bySerial[serial].map((c) => c.bCamera), start, end] };
    // sessionExpiryExempt: an eView MEMBER answering "noauth" here counts as a failed prefetch. It is not
    // the controller session expiring, and the user never asked for these cameras. The controller's own
    // noauth still ends the session, as every other request's does.
    return axios({ method: 'post', url: apiUrl, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, data: reqBody, timeout: 8000, sessionExpiryExempt: isMember })
      .then((res) => {
        const raw = res.data && res.data.result && res.data.result[1];
        return { serial, events: Array.isArray(raw) ? raw : [], failed: !Array.isArray(raw) };
      })
      .catch(() => ({ serial, events: [], failed: true }));
  });
  return Promise.all(requests).then((results) => {
    inFlight.forEach((k) => _prefetchInFlight.delete(k));
    const cur = getState().mcplayback || {};
    // The user changed the window (or a new search replaced the map) while this was in flight.
    if (cur.selectedTimestamp !== start || cur.trackEventsWindow !== windowKey) return null;
    const fetched = {};
    results.forEach(({ serial, events }) => events.forEach((ev) => {
      const k = `${serial}_${ev.bCamera}`;
      (fetched[k] = fetched[k] || []).push(ev);
    }));
    const merged = { ...(cur.trackEvents || {}) };
    const now = new Set((cur.slots || []).filter(Boolean).map(key));
    const okSerials = new Set(results.filter((r) => !r.failed).map((r) => String(r.serial)));
    cams.forEach((c) => {
      const k = key(c);
      // The on-screen cameras belong to the foreground fetch; never overwrite them from here.
      if (now.has(k) || !okSerials.has(String(c.bSerial))) return;
      merged[k] = fetched[k] || [];
    });
    dispatch({ type: SET_MCPB_TRACK_EVENTS, payload: merged, window: windowKey });
    return merged;
  });
};

// ── View-based playback entry (#86bb7phhh) ────────────────────────────
// Playback is VIEW-BASED with no entry form and no playback-local view store: the cameras come from the
// shared Live view (state.view), sized to the view's span up to 16. Cross-viewer reuse — Live and
// playback agree on what a view contains.
export const DEFAULT_MCPB_WINDOW = '1hr'; // open on a recent 1-hour window (iOS MacView parity)

/**
 * loadViewIntoMcpb — materialize the CURRENT SET into the playback grid, then fetch its events. The grid
 * is fully SHARED with Live (#86bb7phhh): the cameras are `state.server.cameras` (already arranged into the
 * current view by ViewActions.selectView) sliced by the shared `currentCamView` set — the SAME slice Live
 * shows — and the layout (conf) is the shared `state.config.conf`. So opening playback shows exactly what
 * Live was showing, and paging the set (CamButtonsView / swipe → camViewChange) re-runs this. Only the
 * VISIBLE set's events are fetched (bounded to ≤ conf cams — fast even on a 32/64-cam NVR). Time is
 * playback-local: RESUME the committed window if one exists this session, else open a recent default
 * window ([now - window, now]). No fetch when the set is empty or before the NVR clock is known.
 */
export const loadViewIntoMcpb = ({ defaultWindow = DEFAULT_MCPB_WINDOW } = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const isEview = isMultiServerSession(state);
    const currentSerial = isEview
      ? (state.server.activeServerSerial || state.server.bSerial)
      : state.server.bSerial;
    // The grid cameras are the current SET — the SAME slice Live shows. getVisibleCameras returns raw
    // state.server.cameras entries; normalize each to the playback slot shape (bCamera/bSerial/sName).
    const currentCamView = state.camera && state.camera.currentCamView;
    const visible = getVisibleCameras(state.server.cameras, currentCamView);
    const slots = visible.map((c) => normalizeSlotCamera(c, { currentSerial }));
    dispatch(setMcpbSlots(slots.length ? slots : [null]));

    if (!slots.some(Boolean)) return Promise.resolve(null); // empty set -> nothing to fetch

    // Once this set's events land, prefetch the rest of the view in the background so the next set
    // swap starts loading video at once (fire-and-forget: the returned promise is the set's own fetch).
    const thenPrefetch = (p) => Promise.resolve(p).then((r) => { dispatch(prefetchViewEvents()); return r; });

    const committed = state.mcplayback && state.mcplayback.selectedTimestamp;
    if (committed) return thenPrefetch(dispatch(refetchMcpbEvents())); // resume this session's window

    const nowTs = (state.utility || {}).nvrTimestampShort || '';
    if (!nowTs) return Promise.resolve(null); // no NVR clock yet -> mount re-runs when it lands
    const anchor = addSeconds(nowTs, -windowSeconds(defaultWindow));
    return thenPrefetch(dispatch(runMcpbSearch({ anchorTimestamp: anchor, window: defaultWindow, filters: { ...DEFAULT_FILTERS } })));
  };

// ── Timeline span (zoom) + window/day paging (#86bb7phhh — ports Java MCTimelineController) ─────────
/**
 * setMcpbSpan — the timeline zoom control (P-N5/N6). Re-window the committed search to a fixed SPAN
 * (1h/4h/8h/24h) anchored on the CLOCK boundary containing the current playhead (Java floorToSpan +
 * "one-window rule"), keeping the playhead on the same wall-clock moment inside the new window.
 *
 * Refetch ONLY when the new window widens past the currently-loaded one (Java keeps its loaded events
 * and re-queries only on a widen). A narrower/equal span is a SUBSET — clipRectPct already clamps the
 * loaded clips to the visible span, so we just re-commit the window + re-anchor the playhead with no
 * round-trip (no loader flash on zoom-in). (A narrow-then-widen-back does one redundant fetch — accepted
 * as the cost of not tracking a separate loaded-range in the store.)
 */
export const setMcpbSpan = (spanSec) => (dispatch, getState) => {
  const span = snapSpan(spanSec);
  const st = getState().mcplayback || {};
  const { selectedTimestamp, window: curWin, playheadOffset, filters } = st;
  if (!selectedTimestamp) return Promise.resolve(null); // nothing searched yet — nothing to re-window
  const playheadTs = addSeconds(selectedTimestamp, Number(playheadOffset) || 0);
  const newStart = floorToSpan(playheadTs, span);
  const newEnd = addSeconds(newStart, span);
  const loadedEnd = addSeconds(selectedTimestamp, windowSeconds(curWin));
  const withinLoaded = secondsBetween(selectedTimestamp, newStart) >= 0
    && secondsBetween(newEnd, loadedEnd) >= 0;
  dispatch(setMcpbSearch({ selectedTimestamp: newStart, window: span, filters }));
  dispatch(setMcpbPlayhead(Math.max(0, secondsBetween(newStart, playheadTs))));
  if (withinLoaded) return Promise.resolve(null); // subset of the loaded events — no fetch
  return dispatch(loadMultiCamEvents({ startTimestamp: newStart, endTimestamp: newEnd }));
};

/**
 * pageMcpbWindow — step the committed window by one WINDOW (unit='window') or one DAY (unit='day') in
 * `dir` (+1 forward / -1 back), then refetch (P-N7). The window DURATION is preserved; the start is
 * clock-aligned to it first (Java MCTimelineWindow.pageBySpan), so day-rollover falls out of addSeconds.
 * The playhead lands at the start of the fresh window.
 */
export const pageMcpbWindow = (dir, unit = 'window') => (dispatch, getState) => {
  const st = getState().mcplayback || {};
  const { selectedTimestamp, window: curWin, filters } = st;
  if (!selectedTimestamp) return Promise.resolve(null);
  const winSec = windowSeconds(curWin);
  const newStart = pageWindow(selectedTimestamp, winSec, dir, unit === 'day' ? 'day' : 'span');
  const newEnd = addSeconds(newStart, winSec);
  dispatch(setMcpbSearch({ selectedTimestamp: newStart, window: curWin, filters }));
  dispatch(setMcpbPlayhead(0));
  return dispatch(loadMultiCamEvents({ startTimestamp: newStart, endTimestamp: newEnd }));
};

/**
 * setMcpbFilters — update the "Find events with" set WITHOUT refetching (P-P4: filter the currently-loaded
 * events client-side; widen the window for more). Keeps the committed window/start; the View re-derives
 * visibleTrackEvents = filterTrackEvents(trackEvents, filters). No-op before a search exists.
 */
export const setMcpbFilters = (filters) => (dispatch, getState) => {
  const { selectedTimestamp, window: win } = getState().mcplayback || {};
  if (!selectedTimestamp) return;
  dispatch(setMcpbSearch({ selectedTimestamp, window: win, filters }));
};

// ── Calendar recording-availability dots (#86bb7phhh — mirrors Java MCPresenceModel) ────────────────
// The "Go to time" calendar shows a green dot on days with footage, fed by the EXISTING
// event.getEventPresence RPC at DAY granularity (no backend change). A small module-scoped cache +
// session unsupported-latch mirrors MCPresenceModel: cache keyed `${sig}|YYYYMM`, both reset when the
// camera-set signature changes (a new view/server re-probes). Older backends that lack the RPC latch
// `unsupported` so the calendar stops re-querying and stays dot-free — still a fully usable date+time
// picker (graceful degrade, David 2026-08-04). DVIEW single-NVR is the primary path; grouping by serial
// already handles eview cross-NVR (each serial's cameras -> its own RPC, dots OR-merged) for later.
let _presenceCache = {};
let _presenceSig = null;
let _presenceUnsupported = false;

/** Drop the presence cache + unsupported-latch (server change / test hygiene). */
export const resetMcpbPresence = () => { _presenceCache = {}; _presenceSig = null; _presenceUnsupported = false; };

/**
 * getMcpbPresence — fetch (or return cached) the recording-day dots for one visible month, merged across
 * the current playback camera set. Returns a Promise<{ dots: boolean[32] (day 1..31), unsupported: bool }>.
 * NEVER rejects — a degraded/absent backend or a transient failure resolves to all-false dots (the caller
 * just shows no dots). Mirrors Java MCPresenceModel.loadMonth (cache-first, day granularity, tz carried).
 *
 * @param {object} opts
 * @param {number} opts.year    full year of the visible month
 * @param {number} opts.month0  0-based month (0=Jan) of the visible month
 */
export const getMcpbPresence = ({ year, month0 } = {}) => (dispatch, getState) => {
  const state = getState();
  const filled = (state.mcplayback.slots || []).filter(Boolean);

  // A camera-set change invalidates everything (dots must match the set) — mirrors Java
  // invalidateIfCameraSetChanged; also re-probes a backend that had latched unsupported (a new
  // server has a fresh capability).
  const sig = cameraSetSignature(filled);
  if (sig !== _presenceSig) { _presenceCache = {}; _presenceUnsupported = false; _presenceSig = sig; }

  if (filled.length === 0) return Promise.resolve({ dots: emptyDayDots(), unsupported: false });

  const key = `${sig}|${monthKey(year, month0)}`;
  if (_presenceCache[key]) return Promise.resolve({ dots: _presenceCache[key], unsupported: _presenceUnsupported });
  if (_presenceUnsupported) return Promise.resolve({ dots: emptyDayDots(), unsupported: true });

  const [start, end] = monthRange(year, month0);

  // CLOUD product: recording-day dots come from cloudapi `/ve/eventpresence` (REST), NOT a per-NVR
  // JSON-RPC. Route through the CloudNvrAdapter — one fetch per filled cloud camera, dots OR-merged.
  // Going over REST is the FIX for the false "Session Expired" (card 86bbqvr6f): the local JSON-RPC POST
  // below noauths in cloud (the JWT is not a local-NVR session) and trips axiosSessionInterceptor into
  // EXPIRE_SESSION. Mirrors loadMultiCamEvents' cloud branch (kind==='cloud' && fetchPlaybackEvents).
  const backend = getBackend(state);
  if (backend && backend.kind === 'cloud' && backend.fetchEventPresence) {
    const cloudCams = filled.filter((c) => c.sDeviceID);
    if (cloudCams.length === 0) return Promise.resolve({ dots: emptyDayDots(), unsupported: false });
    // One /ve fetch per camera; the adapter caches per (device,cam,month) so a grid change only fetches
    // the newly-added cameras. A camera that fails resolves to {ok:false} → dropped from the merge (its
    // dots stay empty, retried next open), never rejecting the whole calendar.
    const reqs = cloudCams.map((cam) =>
      backend.fetchEventPresence(cam.sDeviceID, cam.bCamera, start, end, { year, month0 })
        .then((dots) => ({ ok: true, dots }))
        .catch(() => ({ ok: false })));
    return Promise.all(reqs).then((results) => {
      let dots = emptyDayDots();
      let anyOk = false;
      let anyTransient = false;
      results.forEach((r) => {
        if (r.ok) { anyOk = true; dots = orDayDots(dots, r.dots); }
        else anyTransient = true;
      });
      // Cache the merged month only when every camera answered cleanly (a transient gap retries next open).
      if (anyOk && !anyTransient) _presenceCache[key] = dots;
      return { dots, unsupported: false };
    });
  }

  // LOCAL / eView: tz is carried on the RPC contract (backend buckets by the stored LOCAL prefix, no
  // re-convert). The backend requires a non-empty identifier; default to 'UTC' when unknown, like Java's sTz.
  const tz = (state.utility && state.utility.nvrTimezone) || 'UTC';

  // One RPC per NVR (dview = a single serial). Each returns per-camera day buckets we fold to dots.
  const bySerial = {};
  filled.forEach((cam) => { (bySerial[cam.bSerial] = bySerial[cam.bSerial] || []).push(cam); });

  const reqs = Object.keys(bySerial).map((serial) => {
    const cams = bySerial[serial].map((c) => c.bCamera);
    const { apiUrl, sSess } = resolveServer(state, serial);
    const params = [sSess, cams, tz, start, end, 'day'];
    const reqBody = { jsonrpc: 2.0, method: 'event.getEventPresence', id: 203, params };
    // A member that rejects our user:pass answers "noauth". That is a missing member, not the controller
    // session expiring, so it must not log the user out (same exemption as the events prefetch).
    const isMember = apiUrl !== getApiUrl();
    return axios({
      method: 'post',
      url: apiUrl,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      data: reqBody,
      timeout: 8000,
      sessionExpiryExempt: isMember,
    }).then((res) => {
      const err = res.data && res.data.error;
      if (err) return { kind: isUnsupportedPresenceError(err) ? 'unsupported' : 'transient' };
      const payload = res.data && res.data.result && res.data.result[1];
      // noauth is not an empty month: leave it uncached so the next open asks again.
      if (payload === 'noauth') return { kind: 'transient' };
      return { kind: 'ok', dots: dayDotsFromResult(payload, year, month0) };
    }).catch((e) => ({ kind: isUnsupportedPresenceError(e) ? 'unsupported' : 'transient' }));
  });

  return Promise.all(reqs).then((results) => {
    let dots = emptyDayDots();
    let anyOk = false;
    let anyTransient = false;
    let anyUnsupported = false;
    results.forEach((r) => {
      if (r.kind === 'ok') { anyOk = true; dots = orDayDots(dots, r.dots); }
      else if (r.kind === 'unsupported') anyUnsupported = true;
      else anyTransient = true;
    });
    // No usable data AND the backend reported unknown-method -> latch for the session (stop re-querying).
    if (!anyOk && anyUnsupported) { _presenceUnsupported = true; return { dots: emptyDayDots(), unsupported: true }; }
    // Cache ONLY a clean, complete result (no transient gaps) so a flaky link retries next open — Java
    // leaves a transiently-failed month uncached.
    if (anyOk && !anyTransient) _presenceCache[key] = dots;
    return { dots, unsupported: false };
  });
};
